import Reveal from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import PageSEO from "@/components/seo/PageSEO";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { COMPANY } from "@/lib/company";

// TODO: replace with real directors once confirmed by the client.
// These names/photos are placeholders only.
const LEADERSHIP = [
    {
        name: "Daniel Whitfield",
        role: "Founder & Chief Executive",
        bio: "Chartered civil engineer, 24 years in commercial and civil delivery across the UK and Ireland.",
        img: "https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?auto=format&fit=crop&w=800&q=80",
    },
    {
        name: "Amara Chen",
        role: "Managing Director",
        bio: "Operational lead for programme, cost and quality. Ex-Balfour Beatty, MICE, MSc Construction Management.",
        img: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=800&q=80",
    },
    {
        name: "Rahul Patel",
        role: "Head of Safety & Compliance",
        bio: "CMIOSH-qualified. Owns our safety-first delivery standard on every site we run.",
        img: "https://images.unsplash.com/photo-1767175473698-859bc73e8e64?auto=format&fit=crop&w=800&q=80",
    },
];

const VALUES = [
    {
        title: "Craft",
        text: "Details are not optional. We hire tradespeople who care about the joints you'll never see.",
    },
    {
        title: "Programme",
        text: "Every day on a site costs money. We plan aggressively, and we deliver aggressively.",
    },
    {
        title: "Safety",
        text: "Nobody goes home injured. This is the first sentence in every meeting.",
    },
    {
        title: "Candour",
        text: "If something is slipping, you'll hear it from us first. No spin, no polish, just data.",
    },
];

export default function AboutPage() {
    return (
        <div className="bg-brand-cream" data-testid="about-page">
            <PageSEO
                title="About EB Construction — Manchester Contractor Since 2023"
                description="A young, ambitious Manchester construction firm founded 23 October 2023. UK Home Office licensed sponsor for the Skilled Worker route. Meet our leadership and mission."
                canonicalPath="/about"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "AboutPage",
                    url: "https://elevatebuildconstruction.co.uk/about",
                    name: "About EB Construction",
                    description:
                        "History, mission, and leadership of EB Construction — a Manchester-based UK contractor founded in October 2023.",
                    mainEntity: {
                        "@id": "https://elevatebuildconstruction.co.uk/#organization",
                    },
                }}
            />
            {/* Intro */}
            <section className="border-b border-brand-line py-24 md:py-32">
                <div className="container-x grid gap-16 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <Reveal>
                            <SectionLabel index={0} label="About" />
                            <span className="rule-gold" aria-hidden="true" />
                            <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] md:text-7xl">
                                A builder with a{" "}
                                <span className="accent-italic text-brand-gold">spine.</span>
                            </h1>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <p className="font-mono-tag text-brand-gold">
                                [ FOUNDED {COMPANY.foundedDisplay.toUpperCase()} · COMPANIES HOUSE NO. {COMPANY.companyNumber} ]
                            </p>
                            <p className="mt-6 font-body text-lg leading-relaxed text-brand-steel md:text-xl">
                                ElevateBuild Construction Services Ltd is a young, ambitious Manchester construction firm — founded 23 October 2023 with a Companies House registration and, in year one, a Home Office Skilled Worker sponsor licence.
                            </p>
                            <p className="mt-6 font-body text-lg leading-relaxed text-brand-steel md:text-xl">
                                Our first two years focused on residential and commercial delivery across Cheshire and Greater Manchester, alongside launching our own in-house training academy. We're small enough for directors to still walk every site, and structured enough to run programmes clients can actually rely on.
                            </p>
                            <div
                                className="mt-8 flex items-start gap-4 border-l-2 border-brand-gold bg-white p-6"
                                data-testid="about-sponsor-block"
                            >
                                <ShieldCheck className="mt-1 h-6 w-6 flex-shrink-0 text-brand-gold" />
                                <div>
                                    <p className="font-mono-tag text-brand-steel">
                                        UK Home Office Licensed Sponsor
                                    </p>
                                    <p className="mt-2 font-body text-base leading-relaxed text-brand-charcoal">
                                        ElevateBuild Construction Services Ltd is a UK Home Office licensed sponsor, supporting skilled construction talent from overseas under the Skilled Worker route.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-brand-line pt-8">
                                {[
                                    { k: "23 Oct '23", v: "Founded" },
                                    { k: "24", v: "Projects" },
                                    { k: "£46M", v: "Contract Value" },
                                ].map((s) => (
                                    <div
                                        key={s.v}
                                        data-testid={`about-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <p className="font-industrial text-4xl text-brand-gold">
                                            {s.k}
                                        </p>
                                        <p className="mt-1 font-mono-tag text-brand-steel">
                                            {s.v}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Full-bleed team banner */}
            <section className="relative h-[46vh] min-h-[320px] w-full overflow-hidden border-b border-brand-line md:h-[60vh]">
                <img
                    src="/brand/about-team.jpg"
                    alt="ElevateBuild leadership and site teams on a live UK construction site"
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/10 to-brand-charcoal/40" />
                <div className="absolute inset-x-0 bottom-0">
                    <div className="container-x pb-8">
                        <p className="font-mono-tag text-brand-gold">Our team</p>
                        <p className="accent-italic mt-2 max-w-2xl text-xl text-brand-offwhite md:text-2xl">
                            Directly employed. Directors on site. No revolving-door subcontracting.
                        </p>
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="border-b border-brand-line bg-brand-charcoal py-24 text-brand-offwhite md:py-32">
                <div className="container-x grid gap-10 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <Reveal>
                            <SectionLabel index={1} label="Mission" tone="light" />
                            <span className="rule-gold" aria-hidden="true" />
                            <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-6xl">
                                We build <br />
                                <span className="accent-italic text-brand-gold">
                                    what lasts.
                                </span>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="md:col-span-7">
                        <Reveal delay={0.1}>
                            <p className="font-body text-xl leading-relaxed text-brand-offwhite/85">
                                Our mission is to be a construction partner clients trust with the projects that matter to them — homes, workplaces, community buildings, and civils schemes designed to last.
                            </p>
                            <p className="mt-6 font-body text-lg leading-relaxed text-brand-offwhite/70">
                                We measure ourselves on programme, safety, cost, quality and — critically — on the number of repeat clients who never had to ask a second time.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="border-b border-brand-line bg-brand-paper py-24 md:py-32">
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={2} label="Values" />
                        <span className="rule-gold" aria-hidden="true" />
                        <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-6xl">
                            Four{" "}
                            <span className="accent-italic text-brand-gold">non-negotiables.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid grid-cols-1 border-t border-l border-brand-line md:grid-cols-4">
                        {VALUES.map((v, i) => (
                            <Reveal
                                key={v.title}
                                delay={i * 0.08}
                                className="border-b border-r border-brand-line p-8"
                            >
                                <div data-testid={`about-value-${v.title.toLowerCase()}`}>
                                    <p className="font-mono-tag text-brand-gold">
                                        0{i + 1}
                                    </p>
                                    <h3 className="mt-6 font-display text-3xl font-semibold leading-[1.15]">
                                        {v.title}
                                    </h3>
                                    <p className="mt-4 font-body text-sm leading-relaxed text-brand-steel">
                                        {v.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="bg-brand-cream py-24 md:py-32">
                <div className="container-x">
                    <Reveal>
                        <SectionLabel index={3} label="Leadership" />
                        <span className="rule-gold" aria-hidden="true" />
                        <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.1] md:text-6xl">
                            The{" "}
                            <span className="accent-italic text-brand-gold">team.</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
                        {LEADERSHIP.map((p, i) => (
                            <Reveal
                                key={p.name}
                                delay={i * 0.08}
                                className="border border-brand-line bg-brand-paper"
                            >
                                <div
                                    className="overflow-hidden"
                                    data-testid={`leadership-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="aspect-[4/5] overflow-hidden">
                                        <img
                                            src={p.img}
                                            alt={p.name}
                                            className="h-full w-full object-cover grayscale transition-all duration-[900ms] hover:grayscale-0"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-display text-2xl font-semibold leading-tight">
                                            {p.name}
                                        </h3>
                                        <p className="mt-1 font-mono-tag text-brand-gold">
                                            {p.role}
                                        </p>
                                        <p className="mt-4 font-body text-sm leading-relaxed text-brand-steel">
                                            {p.bio}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.3} className="mt-16 border-t border-brand-line pt-10">
                        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                            <p className="max-w-2xl font-body text-lg text-brand-steel">
                                Talk to a director — not a call centre. We keep a director close to every project we run.
                            </p>
                            <Link
                                to="/contact"
                                className="btn-primary"
                                data-testid="about-cta-contact"
                            >
                                Speak to a director
                                <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
